import {
    Box,
    BoxContent,
    PriceLevel,
    Row,
    Rows,
    Spacer,
    SpacerSize,
    Text,
    TextLevels,
    Title,
    TitleLevels,
    TitleMarkup,
    TypographyAlign,
} from '@bytel/trilogy-react';

import { PRICE_TYPE } from '@app-types/price';
import { CartType } from '@app-types/product';

import { BoxInfo } from '@components/box-info';
import { ListItemWithPrice } from '@components/list-item-with-price';
import { OptionsList } from '@components/options-list';

import { hasPriceTypeProduct } from '@helpers/cart';

import { InlinePrice } from './inline-price';
import PlanPlusOptionHeader from './plan-plus-option-header';

export type CartContentProps = {
    cart: CartType | null;
};

export function CartContent({ cart }: Readonly<CartContentProps>) {
    if (!cart) return null;
    const isSim = cart?.sim?.esim === false;

    return (
        <Rows>
            <Row>
                <BoxInfo header={<PlanPlusOptionHeader total={cart.total} />}>
                    <Title marginless level={TitleLevels.FOUR}>
                        Votre forfait sans engagement
                    </Title>
                    <ListItemWithPrice
                        title={cart.product?.name}
                        subTitle=''
                        amount={cart.product?.prices.final ?? 0}
                        subTitleClassName='has-text-grey-dark'
                        period='mois'
                        titleClassName='!has-text-weight-normal'
                    />
                    {cart.product?.prices.final !== cart.product?.prices.forever ? (
                        <>
                            <Spacer size={SpacerSize.TWO} />
                            <Text level={TextLevels.TWO}>
                                Pendant {cart.product?.prices.duration} mois puis{' '}
                                <InlinePrice amount={cart.product?.prices.forever} period='mois' />
                            </Text>
                        </>
                    ) : null}
                    <OptionsList updatable={false} cart={cart} />
                </BoxInfo>
            </Row>
            <Row>
                {hasPriceTypeProduct(cart, PRICE_TYPE.TODAY) ? (
                    <CartContentByType cart={cart} isSim={isSim} type={PRICE_TYPE.TODAY} />
                ) : null}
            </Row>
            <Row>
                {hasPriceTypeProduct(cart, PRICE_TYPE.DEFERRED) ? (
                    <CartContentByType cart={cart} isSim={isSim} type={PRICE_TYPE.DEFERRED} />
                ) : null}
            </Row>
        </Rows>
    );
}

export function CartContentByType({
    cart,
    isSim,
    type,
    showDefferedBox = false,
}: Readonly<{
    cart: CartType;
    isSim: boolean;
    type: PRICE_TYPE;
    showDefferedBox?: boolean;
}>) {
    return (
        <Row narrow>
            <BoxInfo header={<CartContentHeader cart={cart} type={type} />}>
                {cart.sim?.priceType === type && (
                    <>
                        <Title marginless level={TitleLevels.FIVE}>
                            {isSim ? 'Carte SIM triple découpe' : 'eSIM'}
                        </Title>
                        <ListItemWithPrice
                            subTitle={cart.sim?.esim ? 'Une puce dématérialisée.' : 'Une puce physique.'}
                            amount={cart.sim?.price ?? 0}
                            subTitleClassName='has-text-grey-dark'
                        />
                    </>
                )}
                {cart.deliveryMode && cart.deliveryMode.priceType === type && (
                    <>
                        <Title marginless level={TitleLevels.FIVE}>
                            Frais de livraison
                        </Title>
                        <ListItemWithPrice
                            subTitle='Livraison'
                            amount={cart.deliveryMode?.price ?? 0}
                            subTitleClassName='has-text-grey-dark has-text-weight-normal'
                        />
                    </>
                )}
                {cart.mobileFees && cart.mobileFees.prices.type === type && (
                    <>
                        <Title level={TitleLevels.FIVE} markup={TitleMarkup.H3}>
                            {cart.mobileFees?.name}
                        </Title>
                        <ListItemWithPrice
                            subTitle='Mise en service et configuration de votre nouvelle ligne.'
                            amount={cart.mobileFees?.price ?? 0}
                            subTitleClassName='has-text-grey-dark has-text-weight-normal'
                        />
                    </>
                )}
                {showDefferedBox && (
                    <>
                        <Spacer size={SpacerSize.FOUR} />
                        <Box shadowless flat>
                            <BoxContent>
                                <Text marginless level={TextLevels.TWO} typo={TypographyAlign.TEXT_CENTERED}>
                                    Commandez aujourd’hui !
                                </Text>
                                <Text marginless level={TextLevels.TWO} typo={TypographyAlign.TEXT_CENTERED}>
                                    Payez sur votre première facture
                                </Text>
                            </BoxContent>
                        </Box>
                    </>
                )}
            </BoxInfo>
        </Row>
    );
}

export function CartContentHeader({
    cart,
    type,
}: Readonly<{
    cart: CartType;
    type: PRICE_TYPE;
}>) {
    const title = type === PRICE_TYPE.TODAY ? 'À régler aujourd’hui' : 'À régler sur la 1ère facture';

    const getAmount = () => {
        let amount = 0;

        if (cart.sim?.priceType === type) {
            amount += cart.sim.price ?? 0;
        }
        if (cart.mobileFees?.prices.type === type) {
            amount += cart.mobileFees.price ?? 0;
        }
        if (cart.deliveryMode?.priceType === type) {
            amount += cart.deliveryMode.price ?? 0;
        }

        return amount;
    };
    return (
        <ListItemWithPrice
            mainTitle={title}
            amount={getAmount()}
            titleClassName='has-text-white'
            className='has-text-white'
            priceLevel={PriceLevel.FIVE}
        />
    );
}
